import React from "react";
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  Button,
  Typography,
  IconButton,
  Paper,
  Autocomplete,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { Formik, FieldArray } from "formik";
import { useAnswers, useSaveAnswers } from "apiRequests/useAnswers";
import StepNav from "@/components/StepNav";
import { SPORTS, HOBBIES } from "data/lists";

type Sport = { sport: string; level: string; years: string };
type Club = { name: string; position: string; years: string };
type Volunteer = { organization: string; role: string; years: string };
type Hobby = { hobby: string; years: string };
type Job = { org: string; type: string; pay: string; start: string; end: string };

const Panel: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
    <Typography variant="h6" fontWeight="600" gutterBottom>
      {title}
    </Typography>
    {children}
  </Paper>
);

export default function ExtracurricularActivities() {
  const { data, refetch, isReady } = useAnswers();
  const saveAnswers = useSaveAnswers();

  return !isReady ? (
    <p>Loading...</p>
  ) : (
    <Formik
      initialValues={{
        sports: (data?.personalInformation?.sports as Sport[]) || ([] as Sport[]),
        clubs: (data?.personalInformation?.clubs as Club[]) || ([] as Club[]),
        volunteer:
          (data?.personalInformation?.volunteer as Volunteer[]) ||
          ([] as Volunteer[]),
        jobs: (data?.personalInformation?.jobs as Job[]) || ([] as Job[]),
        // Older records stored hobbies as a flat string[]; normalize those to
        // { hobby, years } so existing data keeps working with the new fields.
        hobbies: (
          (data?.personalInformation?.hobbies as (string | Hobby)[]) || []
        ).map((h) =>
          typeof h === "string" ? { hobby: h, years: "" } : h
        ) as Hobby[],
      }}
      onSubmit={(values) => {
        // personalInformation is stored as a single object that the server
        // overwrites wholesale, so merge our fields into the existing data.
        return saveAnswers({
          personalInformation: {
            ...data?.personalInformation,
            sports: values.sports,
            clubs: values.clubs,
            volunteer: values.volunteer,
            jobs: values.jobs,
            hobbies: values.hobbies,
          },
        }).then((results) => {
          refetch();
          console.log("Answers saved", results);
        });
      }}
    >
      {({ values, handleChange, setFieldValue, handleSubmit, submitForm }) => (
        <form onSubmit={handleSubmit}>
          <Box p={4}>
            {/* 1 - Sports */}
            <Panel title="Sports">
              <FieldArray name="sports">
                {({ push, remove }) => (
                  <>
                    {values.sports.length === 0 && (
                      <Typography color="text.secondary" sx={{ mb: 2 }}>
                        No sports added yet.
                      </Typography>
                    )}
                    {values.sports.map((row, index) => (
                      <Grid
                        container
                        spacing={2}
                        rowSpacing={3}
                        key={index}
                        sx={{ mb: 4 }}
                        alignItems="center"
                      >
                        <Grid item xs={12} sm>
                          <TextField
                            select
                            name={`sports.${index}.sport`}
                            label="Sport"
                            fullWidth
                            value={row.sport || ""}
                            onChange={handleChange}
                          >
                            {SPORTS.map((s) => (
                              <MenuItem key={s} value={s}>
                                {s}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            select
                            name={`sports.${index}.level`}
                            label="Level"
                            fullWidth
                            value={row.level || ""}
                            onChange={handleChange}
                          >
                            <MenuItem value="Varsity">Varsity (V)</MenuItem>
                            <MenuItem value="JV">Junior Varsity (JV)</MenuItem>
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            type="number"
                            name={`sports.${index}.years`}
                            label="Years"
                            fullWidth
                            inputProps={{ min: 0 }}
                            value={row.years || ""}
                            onChange={handleChange}
                          />
                        </Grid>
                        <Grid item xs="auto">
                          <IconButton color="error" onClick={() => remove(index)}>
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    ))}
                    <Button
                      startIcon={<AddIcon />}
                      onClick={() => push({ sport: "", level: "", years: "" })}
                      color="primary"
                      variant="text"
                    >
                      Add Sport
                    </Button>
                  </>
                )}
              </FieldArray>
            </Panel>

            {/* 2 - Clubs */}
            <Panel title="Clubs">
              <FieldArray name="clubs">
                {({ push, remove }) => (
                  <>
                    {values.clubs.length === 0 && (
                      <Typography color="text.secondary" sx={{ mb: 2 }}>
                        No clubs added yet.
                      </Typography>
                    )}
                    {values.clubs.map((row, index) => (
                      <Grid
                        container
                        spacing={2}
                        rowSpacing={3}
                        key={index}
                        sx={{ mb: 4 }}
                        alignItems="center"
                      >
                        <Grid item xs={12} sm>
                          <TextField
                            name={`clubs.${index}.name`}
                            label="Club Name"
                            fullWidth
                            value={row.name}
                            onChange={handleChange}
                          />
                        </Grid>
                        <Grid item xs={12} sm>
                          <TextField
                            name={`clubs.${index}.position`}
                            label="Position / Role"
                            fullWidth
                            value={row.position}
                            onChange={handleChange}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            type="number"
                            name={`clubs.${index}.years`}
                            label="Years"
                            fullWidth
                            inputProps={{ min: 0 }}
                            value={row.years || ""}
                            onChange={handleChange}
                          />
                        </Grid>
                        <Grid item xs="auto">
                          <IconButton color="error" onClick={() => remove(index)}>
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    ))}
                    <Button
                      startIcon={<AddIcon />}
                      onClick={() => push({ name: "", position: "", years: "" })}
                      color="primary"
                      variant="text"
                    >
                      Add Club
                    </Button>
                  </>
                )}
              </FieldArray>
            </Panel>

            {/* 3 - Volunteer */}
            <Panel title="Volunteer">
              <FieldArray name="volunteer">
                {({ push, remove }) => (
                  <>
                    {values.volunteer.length === 0 && (
                      <Typography color="text.secondary" sx={{ mb: 2 }}>
                        No volunteer work added yet.
                      </Typography>
                    )}
                    {values.volunteer.map((row, index) => (
                      <Grid
                        container
                        spacing={2}
                        rowSpacing={3}
                        key={index}
                        sx={{ mb: 4 }}
                        alignItems="center"
                      >
                        <Grid item xs={12} sm>
                          <TextField
                            name={`volunteer.${index}.organization`}
                            label="Organization"
                            fullWidth
                            value={row.organization}
                            onChange={handleChange}
                          />
                        </Grid>
                        <Grid item xs={12} sm>
                          <TextField
                            name={`volunteer.${index}.role`}
                            label="Role / Description"
                            fullWidth
                            value={row.role}
                            onChange={handleChange}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            type="number"
                            name={`volunteer.${index}.years`}
                            label="Years"
                            fullWidth
                            inputProps={{ min: 0 }}
                            value={row.years || ""}
                            onChange={handleChange}
                          />
                        </Grid>
                        <Grid item xs="auto">
                          <IconButton color="error" onClick={() => remove(index)}>
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    ))}
                    <Button
                      startIcon={<AddIcon />}
                      onClick={() => push({ organization: "", role: "", years: "" })}
                      color="primary"
                      variant="text"
                    >
                      Add Volunteer Work
                    </Button>
                  </>
                )}
              </FieldArray>
            </Panel>

            {/* 4 - Jobs/Internships */}
            <Panel title="Jobs/Internships">
              <FieldArray name="jobs">
                {({ push, remove }) => (
                  <>
                    {values.jobs.length === 0 && (
                      <Typography color="text.secondary" sx={{ mb: 2 }}>
                        No jobs or internships added yet.
                      </Typography>
                    )}
                    {values.jobs.map((job, index) => (
                      <Grid
                        container
                        spacing={2}
                        rowSpacing={3}
                        key={index}
                        sx={{ mb: 4 }}
                        alignItems="center"
                      >
                        <Grid item xs={12} sm>
                          <TextField
                            name={`jobs.${index}.org`}
                            label="Organization Name"
                            fullWidth
                            value={job.org}
                            onChange={handleChange}
                          />
                        </Grid>
                        <Grid item xs={12} sm>
                          <TextField
                            select
                            name={`jobs.${index}.type`}
                            label="Type"
                            fullWidth
                            value={job.type || ""}
                            onChange={handleChange}
                          >
                            <MenuItem value="Job">Job</MenuItem>
                            <MenuItem value="Internship">Internship</MenuItem>
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm>
                          <TextField
                            select
                            name={`jobs.${index}.pay`}
                            label="Compensation"
                            fullWidth
                            value={job.pay || ""}
                            onChange={handleChange}
                          >
                            <MenuItem value="Paid">Paid</MenuItem>
                            <MenuItem value="Unpaid">Unpaid</MenuItem>
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm>
                          <TextField
                            type="date"
                            name={`jobs.${index}.start`}
                            label="Start Date"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={job.start}
                            onChange={handleChange}
                          />
                        </Grid>
                        <Grid item xs={12} sm>
                          <TextField
                            type="date"
                            name={`jobs.${index}.end`}
                            label="End Date"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={job.end}
                            onChange={handleChange}
                          />
                        </Grid>
                        <Grid item xs="auto">
                          <IconButton color="error" onClick={() => remove(index)}>
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    ))}
                    <Button
                      startIcon={<AddIcon />}
                      onClick={() =>
                        push({ org: "", type: "", pay: "", start: "", end: "" })
                      }
                      color="primary"
                      variant="text"
                    >
                      Add More
                    </Button>
                  </>
                )}
              </FieldArray>
            </Panel>

            {/* 5 - Hobbies */}
            <Panel title="Hobbies">
              <FieldArray name="hobbies">
                {({ push, remove }) => (
                  <>
                    {values.hobbies.length === 0 && (
                      <Typography color="text.secondary" sx={{ mb: 2 }}>
                        No hobbies added yet.
                      </Typography>
                    )}
                    {values.hobbies.map((row, index) => (
                      <Grid
                        container
                        spacing={2}
                        rowSpacing={3}
                        key={index}
                        sx={{ mb: 4 }}
                        alignItems="center"
                      >
                        <Grid item xs={12} sm>
                          <Autocomplete
                            freeSolo
                            options={HOBBIES}
                            inputValue={row.hobby || ""}
                            onInputChange={(_, v) =>
                              setFieldValue(`hobbies.${index}.hobby`, v)
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Hobby"
                                placeholder="Search or add a hobby"
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            type="number"
                            name={`hobbies.${index}.years`}
                            label="Years"
                            fullWidth
                            inputProps={{ min: 0 }}
                            value={row.years || ""}
                            onChange={handleChange}
                          />
                        </Grid>
                        <Grid item xs="auto">
                          <IconButton color="error" onClick={() => remove(index)}>
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    ))}
                    <Button
                      startIcon={<AddIcon />}
                      onClick={() => push({ hobby: "", years: "" })}
                      color="primary"
                      variant="text"
                    >
                      Add Hobby
                    </Button>
                  </>
                )}
              </FieldArray>
            </Panel>

            <StepNav
              onSave={async () => {
                await submitForm();
                return true;
              }}
            />
          </Box>
        </form>
      )}
    </Formik>
  );
}
